/**
 * A stable identifier for THIS browser profile, used for the two-active-sessions-per-user
 * limit (see services/DeviceSessionService.js and the register_device_session RPC).
 *
 * Ported in concept from ProofioV2's lib/features/auth/session/device_id.dart, which mints a
 * UUID into Keystore-bound preferences and keeps it for the life of the install. localStorage
 * is the web equivalent and fits the threat model well: passing your login to a colleague
 * means they open it on a different machine or profile, which has different localStorage and
 * therefore a different device id. That is precisely the case we want to catch.
 *
 * TWO HONEST LIMITATIONS, neither fatal:
 *   - Clearing site data mints a new id, so the browser looks like a new device and can evict
 *     one of the user's own sessions. Annoying, self-inflicted, recoverable by signing in
 *     again.
 *   - Private/incognito windows always look new. Someone determined can use that to dodge the
 *     limit. This is deterrence against casual password-sharing, not a hard lock — the same
 *     property Proofio has, and unavoidable while the client is the thing being asked.
 *
 * Returns null when storage is unavailable (hardened browsers, blocked third-party contexts).
 * Callers MUST treat null as "cannot check", never as "not allowed" — see the refused vs
 * unreachable distinction in DeviceSessionService.
 */
const DEVICE_ID_KEY = 'tmp_device_id';

const randomId = () => {
  // crypto.randomUUID is unavailable on older Safari and on any non-secure origin.
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  }
  return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;
};

export const getOrCreateDeviceId = () => {
  try {
    const existing = localStorage.getItem(DEVICE_ID_KEY);
    if (existing && existing.trim()) return existing;

    const minted = randomId();
    localStorage.setItem(DEVICE_ID_KEY, minted);
    return minted;
  } catch {
    // Storage blocked. Deliberately NOT falling back to an in-memory id: that would change
    // on every page load and evict the user's real sessions on every refresh.
    return null;
  }
};

/** A short human label for the session list — not an identifier, never used for matching. */
export const getDeviceLabel = () => {
  try {
    return (navigator.userAgent || '').slice(0, 200);
  } catch {
    return null;
  }
};
